import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const ROUTE_COLORS = ["#c9a24a", "#10b981", "#6366f1", "#ef4444", "#0ea5e9", "#f59e0b", "#ec4899"];

export default function FieldMap({ points, routes }) {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const timersRef = useRef([]);

  // Recalcul de la taille un peu plus tard (le bloc vient d'apparaître), seulement si la carte existe encore.
  // Sans ce contrôle, quitter la page pendant le délai faisait planter Leaflet (« _leaflet_pos »).
  const resizeLater = (map, ms) => {
    const id = setTimeout(() => {
      timersRef.current = timersRef.current.filter((t) => t !== id);
      if (mapRef.current === map) map.invalidateSize();
    }, ms);
    timersRef.current.push(id);
  };

  useEffect(() => {
    if (!elRef.current || mapRef.current) return;
    const map = L.map(elRef.current, { scrollWheelZoom: false, attributionControl: true }).setView([43.7384, 7.4246], 13);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19, attribution: "© OpenStreetMap" }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    resizeLater(map, 200);
    return () => {
      // Démontage : recalculs prévus annulés, déplacement en cours arrêté, écouteurs retirés, puis carte détruite.
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
      mapRef.current = null;
      layerRef.current = null;
      map.stop();
      map.off();
      map.remove();
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current, layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    const allLatLng = [];

    (routes || []).forEach((r, ri) => {
      const color = ROUTE_COLORS[ri % ROUTE_COLORS.length];
      const rp = (r.points || []).filter((p) => p.lat && p.lng);
      if (rp.length >= 2) {
        L.polyline(rp.map((p) => [p.lat, p.lng]), { color, weight: 3, opacity: 0.75, dashArray: "7 7" }).addTo(layer);
      }
      rp.forEach((p) => {
        allLatLng.push([p.lat, p.lng]);
        const icon = L.divIcon({ className: "", iconSize: [24, 24], iconAnchor: [12, 12],
          html: `<div style="background:${color};color:#fff;width:24px;height:24px;border-radius:50%;display:grid;place-items:center;font-size:12px;font-weight:700;border:2px solid #fff;box-shadow:0 1px 5px rgba(0,0,0,.35)">${p.order}</div>` });
        L.marker([p.lat, p.lng], { icon }).addTo(layer).bindPopup(`<b>${r.name}</b> · étape ${p.order}<br>${p.type || ""}<br>${p.location || ""}`);
      });
    });

    const pts = (points || []).filter((p) => p.lat && p.lng);
    pts.forEach((p) => {
      allLatLng.push([p.lat, p.lng]);
      const active = p.status === "in_progress" || p.status === "enroute";
      const color = active ? "#10b981" : "#c9a24a";
      const icon = L.divIcon({ className: "", iconSize: [34, 34], iconAnchor: [17, 17],
        html: `<div style="background:${color};color:#fff;width:34px;height:34px;border-radius:50%;display:grid;place-items:center;font-weight:700;box-shadow:0 2px 8px rgba(0,0,0,.35);border:2px solid #fff">${(p.name || "?")[0]}</div>` });
      L.marker([p.lat, p.lng], { icon }).addTo(layer).bindPopup(`<b>${p.name}</b><br>${p.mission || ""}<br>${p.location || ""}`);
    });

    if (allLatLng.length) {
      // Sans animation : les données se rechargent à chaque signal temps réel, et une animation de zoom
      // encore en cours quand on quitte la page s'exécutait sur une carte détruite.
      try { map.fitBounds(L.latLngBounds(allLatLng).pad(0.35), { maxZoom: 15, animate: false }); } catch { /* noop */ }
    }
    resizeLater(map, 150);
  }, [points, routes]);

  return <div ref={elRef} className="h-72 w-full rounded-xl overflow-hidden border border-border relative z-0" data-testid="field-map" />;
}
